import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Shield, FileText, Upload, Globe, Eye, Lock,
  CheckCircle, AlertTriangle, RefreshCw, Network,
  Server, BarChart3, Activity, ChevronRight,
  Search, ShieldAlert, Copy, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { toast } from 'sonner';
import { api } from '@/lib/api';

const SAMPLE_LOGS = `1.2.3.4 - - [15/May/2026:14:32:01 +0000] "GET /robots.txt HTTP/1.1" 200 1024 "-" "GPTBot/1.0 (+https://openai.com/gptbot)"
5.6.7.8 - - [15/May/2026:14:32:05 +0000] "GET /article/123 HTTP/1.1" 200 5432 "-" "Mozilla/5.0 AppleWebKit/537.36 (KHTML, like Gecko) ClaudeBot/1.0"
1.2.3.4 - - [15/May/2026:14:33:01 +0000] "GET /article/456 HTTP/1.1" 200 4321 "-" "GPTBot/1.0 (+https://openai.com/gptbot)"
9.10.11.12 - - [15/May/2026:14:34:11 +0000] "GET /sitemap.xml HTTP/1.1" 200 2222 "-" "PerplexityBot/1.0"`;

const SAMPLE_HOSTS = `chatgpt.com
api.openai.com
claude.ai
gemini.google.com
github.com
googleapis.com
cursor.sh
midjourney.com
internal.corp.com`;

function StatCard({ icon: Icon, label, value, sublabel, color = 'text-blue-400' }) {
  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${color}`} />
        <span className="text-xs text-slate-400 uppercase tracking-wider">{label}</span>
      </div>
      <div className="text-2xl font-bold text-white">{value}</div>
      {sublabel && <div className="text-xs text-slate-500 mt-1">{sublabel}</div>}
    </div>
  );
}

function LogsTab() {
  const [logInput, setLogInput] = useState('');
  const [registerAgents, setRegisterAgents] = useState(true);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleIngest = async () => {
    const lines = logInput.split('\n').filter(l => l.trim());
    if (lines.length === 0) {
      toast.error('Paste at least one log line');
      return;
    }
    setLoading(true);
    try {
      const res = await api.ingestLogs(lines, { registerAgents, source: 'dashboard_paste' });
      setResult(res);
      toast.success(`Parsed ${res.parsed_count} lines, detected ${res.ai_agent_count} AI agent requests`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ingestion failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-5">
        <h3 className="text-white font-semibold mb-2 flex items-center gap-2">
          <FileText className="w-4 h-4 text-blue-400" />
          Access Log Ingestion
        </h3>
        <p className="text-sm text-slate-400 mb-4">
          Paste Nginx / Apache / Cloudflare access logs (Combined or Common Log Format). AI agent requests
          will be extracted and registered as external agents in the control plane.
        </p>
        <Textarea
          value={logInput}
          onChange={e => setLogInput(e.target.value)}
          placeholder={SAMPLE_LOGS}
          className="bg-slate-900 border-slate-700 text-slate-200 font-mono text-xs h-48"
        />
        <div className="flex items-center justify-between mt-3">
          <label className="flex items-center gap-2 text-sm text-slate-300">
            <input
              type="checkbox"
              checked={registerAgents}
              onChange={e => setRegisterAgents(e.target.checked)}
              className="w-4 h-4 accent-blue-500"
            />
            Auto-register detected AI agents
          </label>
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="border-slate-600 text-slate-300 hover:bg-slate-800"
              onClick={() => setLogInput(SAMPLE_LOGS)}
            >
              Load Sample
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              onClick={handleIngest}
              disabled={loading}
            >
              {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
              Ingest Logs
            </Button>
          </div>
        </div>
      </div>

      {result && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <StatCard icon={FileText} label="Parsed" value={result.parsed_count} color="text-blue-400" />
            <StatCard icon={AlertTriangle} label="Skipped" value={result.skipped_count} color="text-amber-400" />
            <StatCard icon={Activity} label="AI Requests" value={result.ai_agent_count} color="text-emerald-400" />
            <StatCard icon={Eye} label="Unique Bots" value={result.unique_ai_bots} color="text-purple-400" />
            <StatCard
              icon={CheckCircle}
              label="Registered"
              value={(result.agents_created || 0) + (result.agents_updated || 0)}
              sublabel={`${result.agents_created || 0} new`}
              color="text-emerald-400"
            />
          </div>

          {Object.keys(result.bot_breakdown || {}).length > 0 && (
            <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-4">
              <h4 className="text-sm font-semibold text-white mb-3">Bot Breakdown</h4>
              <div className="space-y-1.5">
                {Object.entries(result.bot_breakdown)
                  .sort((a, b) => b[1] - a[1])
                  .map(([bot, count]) => (
                    <div key={bot} className="flex items-center gap-3 text-sm">
                      <span className="text-slate-300 w-48 truncate">{bot}</span>
                      <div className="flex-1 bg-slate-900 rounded h-2 overflow-hidden">
                        <div
                          className="h-full bg-blue-500"
                          style={{ width: `${Math.min(100, (count / Math.max(...Object.values(result.bot_breakdown))) * 100)}%` }}
                        />
                      </div>
                      <span className="text-slate-400 w-12 text-right">{count}</span>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {result.top_ips?.length > 0 && (
            <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-4">
              <h4 className="text-sm font-semibold text-white mb-3">Top Source IPs</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                {result.top_ips.map(([ip, count]) => (
                  <div key={ip} className="flex items-center justify-between bg-slate-900 rounded px-3 py-1.5">
                    <span className="font-mono text-slate-300">{ip}</span>
                    <span className="text-slate-500 text-xs">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ShadowAITab() {
  const [hostInput, setHostInput] = useState('');
  const [catalog, setCatalog] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.getShadowAICatalog().then(setCatalog).catch(() => {});
  }, []);

  const handleDetect = async (ingest = false) => {
    const hosts = hostInput.split('\n').map(h => h.trim()).filter(Boolean);
    if (hosts.length === 0) {
      toast.error('Paste at least one hostname');
      return;
    }
    setLoading(true);
    try {
      const fn = ingest ? api.ingestShadowAI : api.detectShadowAI;
      const res = await fn(hosts);
      setResult(res);
      toast.success(`Found ${res.ai_detections} AI tool${res.ai_detections === 1 ? '' : 's'} across ${res.unique_products} product${res.unique_products === 1 ? '' : 's'}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Detection failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {catalog && (
        <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-purple-300 font-medium">Shadow AI catalog</p>
              <p className="text-xs text-purple-400/70 mt-0.5">
                Detecting {catalog.total_entries} known AI products across {catalog.vendors.length} vendors
              </p>
            </div>
            <div className="flex gap-2">
              {Object.entries(catalog.by_category || {}).map(([cat, count]) => (
                <span key={cat} className="px-2.5 py-1 rounded-full bg-purple-500/20 text-purple-300 text-xs font-medium">
                  {cat}: {count}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-5">
        <h3 className="text-white font-semibold mb-2 flex items-center gap-2">
          <Globe className="w-4 h-4 text-purple-400" />
          Shadow AI Detection
        </h3>
        <p className="text-sm text-slate-400 mb-4">
          Paste hostnames (from DNS logs, SNI captures, or HTTP Host headers, one per line) to detect
          unauthorized AI SaaS usage on your network.
        </p>
        <Textarea
          value={hostInput}
          onChange={e => setHostInput(e.target.value)}
          placeholder={SAMPLE_HOSTS}
          className="bg-slate-900 border-slate-700 text-slate-200 font-mono text-xs h-40"
        />
        <div className="flex justify-end gap-2 mt-3">
          <Button
            variant="outline"
            className="border-slate-600 text-slate-300 hover:bg-slate-800"
            onClick={() => setHostInput(SAMPLE_HOSTS)}
          >
            Load Sample
          </Button>
          <Button
            variant="outline"
            className="border-slate-600 text-slate-300 hover:bg-slate-800"
            onClick={() => handleDetect(false)}
            disabled={loading}
          >
            <Eye className="w-4 h-4 mr-2" />
            Detect Only
          </Button>
          <Button
            className="bg-purple-600 hover:bg-purple-700"
            onClick={() => handleDetect(true)}
            disabled={loading}
          >
            {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
            Detect &amp; Register
          </Button>
        </div>
      </div>

      {result && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard icon={Globe} label="Hostnames" value={result.total_hostnames} color="text-blue-400" />
            <StatCard icon={AlertTriangle} label="AI Detected" value={result.ai_detections} color="text-amber-400" />
            <StatCard icon={Eye} label="Products" value={result.unique_products} color="text-purple-400" />
            <StatCard
              icon={CheckCircle}
              label="Registered"
              value={(result.agents_created || 0) + (result.agents_updated || 0)}
              sublabel={`${result.agents_created || 0} new`}
              color="text-emerald-400"
            />
          </div>

          {result.detections?.length > 0 && (
            <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-4">
              <h4 className="text-sm font-semibold text-white mb-3">Detections</h4>
              <div className="space-y-1.5">
                {result.detections.map((d, i) => (
                  <div key={i} className="flex items-center gap-3 px-3 py-2 bg-slate-900/50 rounded text-sm">
                    <span className="font-mono text-slate-300 flex-1 truncate">{d.hostname}</span>
                    <span className="text-white font-medium">{d.product}</span>
                    <span className="text-slate-500 text-xs">{d.vendor}</span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      d.sensitivity === 1 ? 'bg-red-500/20 text-red-300' :
                      d.sensitivity === 2 ? 'bg-amber-500/20 text-amber-300' :
                      'bg-blue-500/20 text-blue-300'
                    }`}>
                      {d.category}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TLSTab() {
  const [hex, setHex] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleInspect = async () => {
    if (!hex.trim()) {
      toast.error('Paste TLS ClientHello bytes (hex)');
      return;
    }
    setLoading(true);
    try {
      const res = await api.inspectTLS(hex);
      setResult(res);
      toast.success(res.sni ? `SNI: ${res.sni}` : 'TLS parsed (no SNI)');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'TLS parse failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-5">
        <h3 className="text-white font-semibold mb-2 flex items-center gap-2">
          <Lock className="w-4 h-4 text-emerald-400" />
          TLS ClientHello Inspection
        </h3>
        <p className="text-sm text-slate-400 mb-4">
          Paste hex-encoded TLS ClientHello bytes (typically captured by a network tap or proxy)
          to extract SNI hostname and compute the JA3 fingerprint. Combine with shadow AI catalog
          to identify encrypted traffic destinations.
        </p>
        <Textarea
          value={hex}
          onChange={e => setHex(e.target.value)}
          placeholder="160301020001000001fc0303..."
          className="bg-slate-900 border-slate-700 text-slate-200 font-mono text-xs h-32"
        />
        <div className="flex justify-end mt-3">
          <Button
            className="bg-emerald-600 hover:bg-emerald-700"
            onClick={handleInspect}
            disabled={loading}
          >
            {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
            Inspect
          </Button>
        </div>
      </div>

      {result && (
        <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-5 space-y-3">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className="text-xs text-slate-500 uppercase">SNI</span>
              <p className="text-white font-mono mt-1">{result.sni || '—'}</p>
            </div>
            <div>
              <span className="text-xs text-slate-500 uppercase">TLS Version</span>
              <p className="text-white font-mono mt-1">{result.tls_version}</p>
            </div>
            <div className="col-span-2">
              <span className="text-xs text-slate-500 uppercase">JA3 Hash</span>
              <p className="text-emerald-400 font-mono mt-1 break-all">{result.ja3_hash}</p>
            </div>
            <div className="col-span-2">
              <span className="text-xs text-slate-500 uppercase">ALPN Protocols</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {(result.alpn_protocols || []).map((p, i) => (
                  <span key={i} className="px-2 py-0.5 rounded bg-slate-700 text-slate-300 text-xs">{p}</span>
                ))}
                {(!result.alpn_protocols || result.alpn_protocols.length === 0) && (
                  <span className="text-slate-500 text-sm">—</span>
                )}
              </div>
            </div>
          </div>

          {result.shadow_ai && (
            <div className="border-t border-slate-700/50 pt-3">
              <span className="text-xs text-slate-500 uppercase mb-2 block">Shadow AI Match</span>
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-400" />
                <span className="text-white font-medium">{result.shadow_ai.product}</span>
                <span className="text-slate-400 text-sm">— {result.shadow_ai.vendor}</span>
                <span className="px-2 py-0.5 rounded bg-purple-500/20 text-purple-300 text-xs">
                  {result.shadow_ai.category}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function IdentityTab() {
  const [providers, setProviders] = useState(null);
  const [identifier, setIdentifier] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.getIdentityProviders().then(setProviders).catch(() => {});
  }, []);

  const handleResolve = async () => {
    if (!identifier.trim()) return;
    setLoading(true);
    try {
      const res = await api.resolveIdentity(identifier.trim());
      setResult(res);
      if (!res.resolved) {
        toast.error('No matching user found');
      } else {
        toast.success(`Resolved via ${res.user.provider}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Resolution failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {providers && (
        <div className="grid grid-cols-2 gap-4">
          <div className={`border rounded-xl p-4 ${
            providers.okta?.configured ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-slate-700 bg-slate-800/30'
          }`}>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-white font-semibold">Okta</h4>
              <span className={`text-xs font-medium ${providers.okta?.configured ? 'text-emerald-400' : 'text-slate-500'}`}>
                {providers.okta?.configured ? 'CONNECTED' : 'NOT CONFIGURED'}
              </span>
            </div>
            <p className="text-xs text-slate-400 font-mono">{providers.okta?.domain || 'Set OKTA_DOMAIN + OKTA_API_TOKEN'}</p>
          </div>
          <div className={`border rounded-xl p-4 ${
            providers.entra_id?.configured ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-slate-700 bg-slate-800/30'
          }`}>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-white font-semibold">Entra ID (Azure AD)</h4>
              <span className={`text-xs font-medium ${providers.entra_id?.configured ? 'text-emerald-400' : 'text-slate-500'}`}>
                {providers.entra_id?.configured ? 'CONNECTED' : 'NOT CONFIGURED'}
              </span>
            </div>
            <p className="text-xs text-slate-400 font-mono">{providers.entra_id?.tenant_id || 'Set ENTRA_TENANT_ID + ENTRA_CLIENT_ID + ENTRA_CLIENT_SECRET'}</p>
          </div>
        </div>
      )}

      <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-5">
        <h3 className="text-white font-semibold mb-2 flex items-center gap-2">
          <Eye className="w-4 h-4 text-blue-400" />
          Resolve User
        </h3>
        <p className="text-sm text-slate-400 mb-4">
          Look up an agent owner against your identity provider to enrich the registry.
        </p>
        <div className="flex gap-2">
          <Input
            value={identifier}
            onChange={e => setIdentifier(e.target.value)}
            placeholder="jane@company.com"
            className="bg-slate-900 border-slate-700 text-slate-200 flex-1"
          />
          <Button
            className="bg-blue-600 hover:bg-blue-700"
            onClick={handleResolve}
            disabled={loading}
          >
            {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
            Resolve
          </Button>
        </div>
      </div>

      {result?.user && (
        <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-5">
          <h4 className="text-white font-semibold mb-3">{result.user.name || '—'}</h4>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-slate-500 text-xs uppercase">Provider</span>
              <p className="text-slate-300 mt-0.5">{result.user.provider}</p>
            </div>
            <div>
              <span className="text-slate-500 text-xs uppercase">Status</span>
              <p className="text-slate-300 mt-0.5">{result.user.status}</p>
            </div>
            <div>
              <span className="text-slate-500 text-xs uppercase">Email</span>
              <p className="text-slate-300 mt-0.5 font-mono text-xs">{result.user.email}</p>
            </div>
            <div>
              <span className="text-slate-500 text-xs uppercase">Department</span>
              <p className="text-slate-300 mt-0.5">{result.user.department || '—'}</p>
            </div>
            <div>
              <span className="text-slate-500 text-xs uppercase">Title</span>
              <p className="text-slate-300 mt-0.5">{result.user.title || '—'}</p>
            </div>
            <div>
              <span className="text-slate-500 text-xs uppercase">User ID</span>
              <p className="text-slate-300 mt-0.5 font-mono text-xs">{result.user.user_id}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function HunterTab() {
  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [runResult, setRunResult] = useState(null);
  const [severity, setSeverity] = useState('');

  const startScan = async () => {
    setLoading(true);
    try {
      const result = await api.startHunterRun('both');
      setRunResult(result);
      toast.success(`Hunter scan started: ${result.run_id}`);
      setTimeout(async () => {
        try {
          const run = await api.getHunterRun(result.run_id);
          setRunResult(run);
          setFindings(run.findings || []);
        } catch (e) { /* poll again later */ }
        setLoading(false);
      }, 2000);
    } catch (err) {
      toast.error('Failed to start hunter scan');
      setLoading(false);
    }
  };

  const loadFindings = async () => {
    try {
      const data = await api.getHunterFindings(50, severity || null);
      setFindings(data.findings || []);
    } catch (err) {
      toast.error('Failed to load findings');
    }
  };

  useEffect(() => { loadFindings(); }, []);

  const sevColor = (s) => {
    if (s === 'critical') return 'text-red-400 bg-red-500/10 border-red-500/20';
    if (s === 'high') return 'text-orange-400 bg-orange-500/10 border-orange-500/20';
    if (s === 'medium') return 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20';
    return 'text-blue-400 bg-blue-500/10 border-blue-500/20';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button onClick={startScan} disabled={loading} className="bg-purple-600 hover:bg-purple-700 text-white">
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
          {loading ? 'Scanning...' : 'Run Hunter Scan'}
        </Button>
        <select value={severity} onChange={e => setSeverity(e.target.value)}
          className="bg-slate-800 border border-slate-700 text-slate-300 rounded px-3 py-2 text-sm">
          <option value="">All Severities</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
        <Button onClick={loadFindings} variant="outline" className="border-slate-700 text-slate-300">
          <RefreshCw className="w-4 h-4 mr-2" />Refresh
        </Button>
      </div>

      {runResult && (
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <span className="font-mono">{runResult.run_id}</span>
            <span className={`px-2 py-0.5 rounded text-xs ${runResult.status === 'done' ? 'bg-green-500/10 text-green-400' : runResult.status === 'failed' ? 'bg-red-500/10 text-red-400' : 'bg-yellow-500/10 text-yellow-400'}`}>
              {runResult.status}
            </span>
            {runResult.finding_count !== undefined && <span>{runResult.finding_count} findings</span>}
          </div>
        </div>
      )}

      {findings.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          <Search className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No findings yet. Run a hunter scan or check back later.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {findings.map((f, i) => (
            <div key={f.finding_id || i} className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
              <div className="flex items-center gap-3 mb-2">
                <span className={`px-2 py-0.5 rounded text-xs border ${sevColor(f.severity)}`}>{f.severity}</span>
                <span className="text-white font-medium">{f.product || f.agent_name || f.type}</span>
                <span className="text-slate-500 text-xs">{f.type}</span>
              </div>
              {f.evidence && (
                <div className="text-xs text-slate-400 font-mono mt-1">
                  {f.evidence.slice(0, 3).join(', ')}
                  {f.evidence.length > 3 && ` +${f.evidence.length - 3} more`}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LLMSafetyTab() {
  const [text, setText] = useState('');
  const [direction, setDirection] = useState('input');
  const [redact, setRedact] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const scanText = async () => {
    if (!text.trim()) return;
    setLoading(true);
    try {
      const data = await api.scanLLMSafety(text, direction, redact);
      setResult(data);
      if (data.finding_count > 0) {
        toast.warning(`Found ${data.finding_count} issue(s)`);
      } else {
        toast.success('No issues detected');
      }
    } catch (err) {
      toast.error('Scan failed');
    }
    setLoading(false);
  };

  const sevColor = (s) => {
    if (s === 'crit') return 'text-red-400 bg-red-500/10 border-red-500/20';
    if (s === 'high') return 'text-orange-400 bg-orange-500/10 border-orange-500/20';
    if (s === 'medium') return 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20';
    return 'text-blue-400 bg-blue-500/10 border-blue-500/20';
  };

  const catIcon = (c) => {
    if (c === 'injection') return '💉';
    if (c === 'pii') return '👤';
    if (c === 'secret') return '🔑';
    return '⚠️';
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <label className="text-sm text-slate-400 mb-2 block">Text to Scan</label>
          <Textarea
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder="Paste text to scan for prompt injection, PII, or secrets..."
            className="bg-slate-800/50 border-slate-700 text-slate-200 font-mono text-sm min-h-[200px]"
          />
          <div className="flex items-center gap-3 mt-3">
            <select value={direction} onChange={e => setDirection(e.target.value)}
              className="bg-slate-800 border border-slate-700 text-slate-300 rounded px-3 py-2 text-sm">
              <option value="input">Input (prompt)</option>
              <option value="output">Output (response)</option>
            </select>
            <label className="flex items-center gap-2 text-sm text-slate-400">
              <input type="checkbox" checked={redact} onChange={e => setRedact(e.target.checked)}
                className="rounded border-slate-700" />
              Redact findings
            </label>
            <Button onClick={scanText} disabled={loading || !text.trim()} className="bg-purple-600 hover:bg-purple-700 text-white">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldAlert className="w-4 h-4 mr-2" />}
              Scan
            </Button>
          </div>
        </div>

        <div>
          {result && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className={`rounded-xl p-3 border ${result.has_injection ? 'bg-red-500/10 border-red-500/20' : 'bg-slate-800/50 border-slate-700/50'}`}>
                  <div className="text-xs text-slate-400 mb-1">Injection</div>
                  <div className={`text-lg font-bold ${result.has_injection ? 'text-red-400' : 'text-green-400'}`}>
                    {result.has_injection ? 'DETECTED' : 'Clean'}
                  </div>
                </div>
                <div className={`rounded-xl p-3 border ${result.has_pii ? 'bg-orange-500/10 border-orange-500/20' : 'bg-slate-800/50 border-slate-700/50'}`}>
                  <div className="text-xs text-slate-400 mb-1">PII</div>
                  <div className={`text-lg font-bold ${result.has_pii ? 'text-orange-400' : 'text-green-400'}`}>
                    {result.has_pii ? 'FOUND' : 'Clean'}
                  </div>
                </div>
                <div className={`rounded-xl p-3 border ${result.has_secrets ? 'bg-red-500/10 border-red-500/20' : 'bg-slate-800/50 border-slate-700/50'}`}>
                  <div className="text-xs text-slate-400 mb-1">Secrets</div>
                  <div className={`text-lg font-bold ${result.has_secrets ? 'text-red-400' : 'text-green-400'}`}>
                    {result.has_secrets ? 'LEAKED' : 'Clean'}
                  </div>
                </div>
              </div>

              {result.findings.length > 0 && (
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {result.findings.map((f, i) => (
                    <div key={i} className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <span>{catIcon(f.category)}</span>
                        <span className={`px-2 py-0.5 rounded text-xs border ${sevColor(f.severity)}`}>{f.severity}</span>
                        <span className="text-white text-sm">{f.description}</span>
                      </div>
                      <div className="text-xs text-slate-500 font-mono">{f.matched_text}</div>
                    </div>
                  ))}
                </div>
              )}

              {result.redacted_text && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-slate-400">Redacted Text</span>
                    <Button variant="ghost" size="sm" onClick={() => { navigator.clipboard.writeText(result.redacted_text); toast.success('Copied!'); }}>
                      <Copy className="w-3 h-3 mr-1" />Copy
                    </Button>
                  </div>
                  <pre className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-3 text-xs text-slate-300 whitespace-pre-wrap font-mono">
                    {result.redacted_text}
                  </pre>
                </div>
              )}
            </div>
          )}

          {!result && (
            <div className="text-center py-12 text-slate-500">
              <ShieldAlert className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Paste text on the left and click Scan to detect prompt injection, PII, and secrets.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function DetectionPage() {
  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-white text-lg">AgentCheck</span>
          </Link>
          <nav className="flex items-center gap-6">
            <Link to="/" className="text-slate-400 hover:text-white transition-colors text-sm">Site Scan</Link>
            <Link to="/agents" className="text-slate-400 hover:text-white transition-colors text-sm">Agent Control Plane</Link>
            <span className="text-white font-medium text-sm">Detection</span>
          </nav>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-xl bg-purple-500/10 border border-purple-500/20">
              <Network className="w-6 h-6 text-purple-400" />
            </div>
            <h1 className="text-2xl font-bold text-white">Network Detection &amp; Identity</h1>
          </div>
          <p className="text-slate-400 text-sm">
            Detect external AI agents from access logs, identify shadow AI usage from DNS/SNI signals,
            and enrich agent owners with Okta or Entra ID.
          </p>
        </div>

        <Tabs defaultValue="logs" className="w-full">
          <TabsList className="bg-slate-800 border border-slate-700 mb-6">
            <TabsTrigger value="logs" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400">
              <FileText className="w-4 h-4 mr-2" />Access Logs
            </TabsTrigger>
            <TabsTrigger value="shadow" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400">
              <Globe className="w-4 h-4 mr-2" />Shadow AI
            </TabsTrigger>
            <TabsTrigger value="tls" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400">
              <Lock className="w-4 h-4 mr-2" />TLS Inspect
            </TabsTrigger>
            <TabsTrigger value="identity" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400">
              <Eye className="w-4 h-4 mr-2" />Identity
            </TabsTrigger>
            <TabsTrigger value="hunter" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400">
              <Search className="w-4 h-4 mr-2" />Hunter
            </TabsTrigger>
            <TabsTrigger value="llm-safety" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400">
              <ShieldAlert className="w-4 h-4 mr-2" />LLM Safety
            </TabsTrigger>
          </TabsList>

          <TabsContent value="logs"><LogsTab /></TabsContent>
          <TabsContent value="shadow"><ShadowAITab /></TabsContent>
          <TabsContent value="tls"><TLSTab /></TabsContent>
          <TabsContent value="identity"><IdentityTab /></TabsContent>
          <TabsContent value="hunter"><HunterTab /></TabsContent>
          <TabsContent value="llm-safety"><LLMSafetyTab /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
